import { 
  collection, 
  addDoc, 
  getDocs, 
  getDoc, 
  doc, 
  updateDoc, 
  deleteDoc,
  query,
  orderBy,
  limit,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';

// Blog posts functions
export const getAllBlogPosts = async () => {
  const postsCollection = collection(db, 'blogPosts');
  const q = query(postsCollection, orderBy('createdAt', 'desc'));
  const postsSnapshot = await getDocs(q);
  return postsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const getBlogPostById = async (id) => {
  const postDoc = doc(db, 'blogPosts', id);
  const postSnapshot = await getDoc(postDoc);
  if (postSnapshot.exists()) {
    return { id: postSnapshot.id, ...postSnapshot.data() };
  }
  return null;
};

export const addBlogPost = async (postData) => {
  const postsCollection = collection(db, 'blogPosts');
  const docRef = await addDoc(postsCollection, {
    ...postData,
    createdAt: new Date()
  });
  return docRef.id;
};

export const updateBlogPost = async (id, postData) => {
  const postDoc = doc(db, 'blogPosts', id);
  await updateDoc(postDoc, postData);
};

export const deleteBlogPost = async (id) => {
  const postDoc = doc(db, 'blogPosts', id);
  await deleteDoc(postDoc);
};

// Categories functions
export const getAllCategories = async () => {
  const categoriesCollection = collection(db, 'categories');
  const q = query(categoriesCollection, orderBy('name', 'asc'));
  const categoriesSnapshot = await getDocs(q);
  return categoriesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const addCategory = async (categoryData) => {
  const categoriesCollection = collection(db, 'categories');
  const docRef = await addDoc(categoriesCollection, categoryData);
  return docRef.id;
};

export const updateCategory = async (id, categoryData) => {
  const categoryDoc = doc(db, 'categories', id);
  await updateDoc(categoryDoc, categoryData);
};

export const deleteCategory = async (id) => {
  const categoryDoc = doc(db, 'categories', id);
  await deleteDoc(categoryDoc);
};

// Testimonials functions
export const getAllTestimonials = async () => {
  const testimonialsCollection = collection(db, 'testimonials');
  const q = query(testimonialsCollection, orderBy('createdAt', 'desc'));
  const testimonialsSnapshot = await getDocs(q);
  return testimonialsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const getTestimonialById = async (id) => {
  const testimonialDoc = doc(db, 'testimonials', id);
  const testimonialSnapshot = await getDoc(testimonialDoc);
  if (testimonialSnapshot.exists()) {
    return { id: testimonialSnapshot.id, ...testimonialSnapshot.data() };
  }
  return null;
};

export const addTestimonial = async (testimonialData) => {
  const testimonialsCollection = collection(db, 'testimonials');
  const docRef = await addDoc(testimonialsCollection, {
    ...testimonialData,
    createdAt: new Date(),
    approved: false // Default to not approved
  });
  return docRef.id;
};

export const updateTestimonial = async (id, testimonialData) => {
  const testimonialDoc = doc(db, 'testimonials', id);
  await updateDoc(testimonialDoc, testimonialData);
};

export const deleteTestimonial = async (id) => {
  const testimonialDoc = doc(db, 'testimonials', id);
  await deleteDoc(testimonialDoc);
};

// Contact messages functions
export const submitContactMessage = async (messageData) => {
  const messagesCollection = collection(db, 'contactMessages');
  const docRef = await addDoc(messagesCollection, {
    ...messageData,
    createdAt: new Date(),
    status: 'new', // Default status
    read: false // Initially unread
  });
  return docRef.id;
};

export const getAllContactMessages = async () => {
  const messagesCollection = collection(db, 'contactMessages');
  const q = query(messagesCollection, orderBy('createdAt', 'desc'));
  const messagesSnapshot = await getDocs(q);
  return messagesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const getContactMessageById = async (id) => {
  const messageDoc = doc(db, 'contactMessages', id);
  const messageSnapshot = await getDoc(messageDoc);
  if (messageSnapshot.exists()) {
    return { id: messageSnapshot.id, ...messageSnapshot.data() };
  }
  return null;
};

export const updateContactMessage = async (id, messageData) => {
  const messageDoc = doc(db, 'contactMessages', id);
  await updateDoc(messageDoc, messageData);
};

export const deleteContactMessage = async (id) => {
  const messageDoc = doc(db, 'contactMessages', id);
  await deleteDoc(messageDoc);
};

export const markContactMessageAsRead = async (id) => {
  const messageDoc = doc(db, 'contactMessages', id);
  await updateDoc(messageDoc, {
    read: true,
    status: 'read' // Update status to 'read'
  });
};

export const getUnreadContactMessagesCount = async () => {
  const messagesCollection = collection(db, 'contactMessages');
  const q = query(messagesCollection, where('read', '==', false));
  const messagesSnapshot = await getDocs(q);
  return messagesSnapshot.size;
};

// Slider functions
export const getAllSlides = async () => {
  try {
    const slidesCollection = collection(db, 'slides');
    const q = query(slidesCollection, orderBy('order', 'asc'));
    const slidesSnapshot = await getDocs(q);
    return slidesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting all slides:', error);
    throw error;
  }
};

export const getActiveSlides = async () => {
  try {
    const slidesCollection = collection(db, 'slides');
    const q = query(slidesCollection, where('active', '==', true), orderBy('order', 'asc'));
    const slidesSnapshot = await getDocs(q);
    return slidesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting active slides:', error);
    throw error;
  }
};

export const addSlide = async (slideData) => {
  try {
    const slidesCollection = collection(db, 'slides');
    const docRef = await addDoc(slidesCollection, {
      ...slideData,
      createdAt: serverTimestamp(),
      active: slideData.active !== undefined ? slideData.active : true,
      order: slideData.order || 0
    });
    return docRef.id;
  } catch (error) {
    console.error('Error adding slide:', error);
    throw error;
  }
};

export const updateSlide = async (id, slideData) => {
  try {
    const slideDoc = doc(db, 'slides', id);
    await updateDoc(slideDoc, {
      ...slideData,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating slide:', error);
    throw error;
  }
};

export const deleteSlide = async (id) => {
  try {
    const slideDoc = doc(db, 'slides', id);
    await deleteDoc(slideDoc);
  } catch (error) {
    console.error('Error deleting slide:', error);
    throw error;
  }
};